/**
 * activityActions.js - what one Activity log entry can do besides being read
 * (Sharif, 10/8/26: "add a revert action at the end of every action, and when
 * I click an action that was changed I should be able to jump there").
 *
 * PURE: an audit row in (GET /api/audit, written by server/middleware/audit.js:
 * method, path and a `detail` holding `changes: { column: { from, to } }`,
 * `snapshot`, `created` and route notes), plans out. The screen
 * (views/activity/ActivityEntry.jsx) does the reading and the sending.
 *
 *   jumpOf(row)            where the entry's record lives - a deep link the
 *                          app already reads (#/opportunities?project=,
 *                          #/projects?project=, #/crm?account=, #/crm?contact=,
 *                          #/ksa-overview?project=, #/engineers?engineer=,
 *                          #/crm-accounts?record=, #/team ...), or why there
 *                          is nothing to open
 *   resolveJump(jump, ctx) that link checked against the records the app has
 *                          loaded: a deleted record says so instead
 *   revertOf(row)          how the entry is undone:
 *                            update  send the `from` values back through the
 *                                    SAME route the change used (its area
 *                                    gate, validation, live updates and the
 *                                    audit log all apply - the revert is a new
 *                                    entry), with the r50 `_expect` guard
 *                                    where the route has one
 *                            delete  a created record removed through its own
 *                                    DELETE route
 *                            null    nothing can be undone, and `reason` says why
 *   planUpdate(row, cur)   the request, and the lines the confirm step shows,
 *                          against the record's values now (`cur`)
 *   deletePlan(row)        the same for a create
 *   mayRevert(rv, access)  whether this person's access lets them (the
 *                          server decides anyway)
 *
 * What the log cannot give back is never guessed: a value it cut at 80
 * characters, a JSON field, a time stamp, a list of several values, or a
 * deleted record (the log keeps a few identifying fields, never the whole row).
 */
import FIELDS from './revertFields.json';
import { isProjectRow } from '../../data/projectStatus';
import { opportunityHash, projectHash, contactHash } from '../../components/crm/nextStep';
import { AREAS, LEVEL_LABELS, canEdit, canDelete, hasArea, roleLabel } from '../../data/roles';

/* ── reading a logged value ──────────────────────────────────────────── */

/* Column name -> label a human reads: po_value -> "PO Value", pm -> "PM". */
export const FIELD_LABELS = {
  po_value: 'PO Value',
  po_number: 'PO Number',
  po_date_issued: 'PO Date',
  quoted_price: 'Quoted Price',
  quoted_labor_cost: 'Quoted Labor Cost',
  quoted_expenses: 'Quoted Expenses',
  invoiced_already: 'Invoiced',
  invoice_numbers: 'Invoice Numbers',
  latest_invoice_date: 'Latest Invoice Date',
  money_leftover: 'Leftover',
  quote_letter: 'Quote #',
  quote_date: 'Quote Date',
  approval_manager: 'Approval Manager',
  pm: 'PM',
  global_project_number: 'Global Project #',
  sub_customer: 'Sub Customer',
  project_number: 'Project #',
  doc_type: 'Label Type',
  doc_ref: 'Label Number',
  billing_type: 'Billing Type',
  is_user_created: 'User-created',
};
export const fieldLabel = (col) =>
  FIELD_LABELS[col] ||
  String(col)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/* The audit logger cuts every value at 80 characters (middleware/audit.js
   trunc), so one that long may not be whole. */
export const LOG_MAX = 80;

const MONTHS = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};
// How a DATE column reaches the log: pg hands back a Date, which the logger
// writes with String(): "Wed Oct 07 2026 00:00:00 GMT-0400 (Eastern Daylight Time)".
const JS_DATE =
  /^(?:Sun|Mon|Tue|Wed|Thu|Fri|Sat) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{2}) (\d{4}) (\d{2}:\d{2}:\d{2}) GMT/;
const PLAIN_NUMBER = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;
const DAY = /^\d{4}-\d{2}-\d{2}/;

/** A logged JS date at midnight -> 'YYYY-MM-DD'; anything else as it is. */
export function logDay(v) {
  const m = String(v ?? '').match(JS_DATE);
  return m && m[4] === '00:00:00' ? `${m[3]}-${MONTHS[m[1]]}-${m[2]}` : null;
}

/**
 * A logged value as a route takes it back: { ok, value } or { ok: false, why }.
 * kind: 'text' (default), 'list' (the logger joined it with ", "), 'bool'.
 */
export function restoreValue(v, kind = 'text') {
  if (v === null || v === undefined) return { ok: true, value: kind === 'list' ? [] : null };
  const s = String(v);
  if (s.length >= LOG_MAX) return { ok: false, why: 'too long for the log to have kept whole' };
  if (s === '[object Object]') return { ok: false, why: 'holds several values the log does not keep' };
  const day = logDay(s);
  if (day) return { ok: true, value: day };
  if (JS_DATE.test(s)) return { ok: false, why: 'a time stamp the log keeps only to the second' };
  if (kind === 'list') return { ok: true, value: s === '' ? [] : s.split(', ') };
  if (kind === 'bool') {
    if (s === 'true' || s === 'false') return { ok: true, value: s === 'true' };
    return { ok: false, why: 'not a yes / no value' };
  }
  return { ok: true, value: s };
}

/* One value as text for comparing: a list joined as the logger joins it, a
   logged date as its day, blanks as ''. */
function plain(v) {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(String).join(', ');
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  const s = String(v);
  return (logDay(s) || s).trim();
}

/**
 * Whether a record's value now (`cur`, from the API) is the logged one
 * (`logged`): blanks are one value, numbers compare as numbers ('52600.00' is
 * 52600), dates on the day, lists as the logger wrote them; a logged value
 * the logger cut short (LOG_MAX) matches anything that starts with it.
 */
export function sameValue(cur, logged) {
  const a = plain(cur);
  const b = plain(logged);
  if (!a || !b) return !a && !b;
  if (String(logged).length >= LOG_MAX) return a.startsWith(b);
  if (PLAIN_NUMBER.test(a) && PLAIN_NUMBER.test(b)) return Number(a) === Number(b);
  if (DAY.test(a) && DAY.test(b)) return a.slice(0, 10) === b.slice(0, 10);
  return a === b;
}

/* Values inside a change line: blanks say "blank", money-ish strings get
   thousands separators, everything else passes through. A PO, quote, invoice
   or label number is an identifier, not an amount: "100194349", never
   "100,194,349" - that is how it is searched for. A logged date reads as its
   day ("2026-10-07"), not as the server's long date text. */
const ID_COLS = new Set(['po_number', 'quote_letter', 'invoice_numbers', 'doc_ref', 'project_number', 'global_project_number']);
// A margin is a percent with two decimals, like every other one in the app:
// since r50 the server stores it unrounded (64.28571428571429).
const PERCENT_COLS = new Set(['quoted_gross_margin']);
// a DATE column as the API sends it now: "2026-10-07T04:00:00.000Z"
const ISO_STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
export function fmtVal(v, col) {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return '(blank)';
  if (Array.isArray(v)) return v.join(', ');
  if (col === 'role') return roleLabel(v);
  if (ID_COLS.has(col)) return String(v);
  const day = logDay(v) || (ISO_STAMP.test(String(v)) ? String(v).slice(0, 10) : null);
  if (day) return day;
  const n = Number(v);
  if (PERCENT_COLS.has(col) && Number.isFinite(n)) return `${n.toFixed(2)}%`;
  if (Number.isFinite(n) && /^-?\d+(\.\d+)?$/.test(String(v).trim()) && Math.abs(n) >= 1000) return n.toLocaleString('en-US');
  return String(v);
}

/* "Q5306 · Jones Plastics · Four weeks onsite" from a snapshot/created blob. */
export function identText(s) {
  if (!s) return '';
  const q = s.quote_letter || s.quoteLetter;
  return [
    q ? `Q${q}` : null,
    s.customer || s.company || null,
    s.name || null,
    s.filename || null,
    s.email || null,
    s.description ? `"${String(s.description).slice(0, 50)}"` : null,
  ]
    .filter(Boolean)
    .slice(0, 3)
    .join(' · ');
}

const enc = encodeURIComponent;
const findId = (list, id) => (Array.isArray(list) ? list.find((x) => x && String(x.id) === String(id)) || null : null);
const segs = (path) => String(path || '').split('?')[0];

/* ── jump: where the entry's record lives ────────────────────────────── */

const accountHash = (id) => `#/crm?account=${enc(id)}`;
const none = (reason) => ({ reason });
const gone = (what) => none(`This ${what} was deleted - there is nothing to open.`);
const fixed = (href, tab) => ({ href, tab });
const toProject = (id) => (id ? { target: { type: 'project', id: String(id) } } : none('The log does not say which record this was on.'));
const toKsa = (id) =>
  id ? { target: { type: 'ksa', id: String(id) } } : none('The log does not say which GLOBAL KSA record this was on.');
const STAFFING_NOUN = { accounts: 'account', contacts: 'contact', leads: 'lead', activities: 'activity', notes: 'note' };

/** The record a task is on: an account or contact by its type, else whatever record has that id. */
function toTaskHome(t) {
  if (!t || !t.entity_id) return none('The log does not say which record this task was on.');
  const type = t.entity_type === 'account' ? 'account' : t.entity_type === 'contact' ? 'contact' : 'record';
  return { target: { type, id: String(t.entity_id) }, task: true };
}

/** Where an entry's record lives, by its path: { target } | { href, tab } | { reason }. */
export function jumpOf(row) {
  const { method } = row;
  const path = segs(row.path);
  const d = row.detail || {};
  const made = d.created || {};
  const snap = d.snapshot || {};
  const del = method === 'DELETE';
  let m;

  // GLOBAL KSA
  if (/^\/api\/ksa\/projects$/.test(path)) return toKsa(made.id);
  if ((m = path.match(/^\/api\/ksa\/projects\/([^/]+)$/))) return del ? gone('GLOBAL KSA record') : toKsa(decodeURIComponent(m[1]));
  if (
    (m = path.match(/^\/api\/ksa\/(?:projects|attachments)\/([^/]+)\/(?:comments|purchases|attachments|folders)(?:\/[^/]+)?$/)) &&
    m[1] !== 'folders'
  )
    return toKsa(decodeURIComponent(m[1]));
  if (/^\/api\/ksa\/attachments\/folders\/[^/]+$/.test(path)) return none('The log does not say which record this folder was on.');
  if (/^\/api\/ksa\/attachments\/[^/]+$/.test(path)) return toKsa(snap.project_id);
  if (/^\/api\/ksa\/crm-actions(\/[^/]+)?$/.test(path)) return toKsa(made.entity_id || snap.entity_id);
  if ((m = path.match(/^\/api\/ksa\/quotes\/drafts\/([^/]+)$/))) return toKsa(decodeURIComponent(m[1]));
  if (/^\/api\/ksa\//.test(path)) return fixed('#/ksa-overview', 'ksa-overview');

  // the invoice ledger (10/11/26): an invoice added from the record editor
  // opens its project; the Finance Invoice tab's own entries open that tab
  if ((m = path.match(/^\/api\/projects\/([^/]+)\/invoices$/))) return toProject(decodeURIComponent(m[1]));
  if (/^\/api\/finance-invoices\b/.test(path)) {
    const pid = made.project_id || snap.project_id;
    return fixed(pid ? `#/finance-invoices?project=${enc(pid)}` : '#/finance-invoices', 'finance-invoices');
  }

  // opportunities and projects, their notes, files and folders
  if (/^\/api\/projects$/.test(path)) return toProject(made.id);
  if (/^\/api\/(projects|attachments)\/folders\/[^/]+$/.test(path)) return none('The log does not say which record this folder was on.');
  if ((m = path.match(/^\/api\/projects\/([^/]+)$/))) return del ? gone('record') : toProject(decodeURIComponent(m[1]));
  if (
    (m = path.match(
      /^\/api\/(?:projects|attachments)\/([^/]+)\/(?:comments|attachments|folders|assign-engineer|unassign-engineer)(?:\/[^/]+)?$/
    ))
  )
    return toProject(decodeURIComponent(m[1]));
  if (/^\/api\/attachments\/[^/]+$/.test(path)) return toProject(snap.project_id);
  if ((m = path.match(/^\/api\/quotes\/drafts\/([^/]+)$/))) return toProject(decodeURIComponent(m[1]));
  if (/^\/api\/quote-rates/.test(path)) return fixed('#/engineers?view=rates', 'engineers');
  if (/^\/api\/quotes\//.test(path)) return none('A quote document - it was downloaded, not kept, so there is nothing to open.');

  // Spare Parts: the part, the robot, or the stock list
  if (/^\/api\/spare-parts\/parts$/.test(path))
    return made.id ? fixed(`#/spare-parts?part=${enc(made.id)}`, 'spare-parts') : fixed('#/spare-parts', 'spare-parts');
  if ((m = path.match(/^\/api\/spare-parts\/parts\/(\d+)(\/price-check)?$/)))
    return del ? gone('spare part') : fixed(`#/spare-parts?part=${m[1]}`, 'spare-parts');
  if (/^\/api\/spare-parts\/robots$/.test(path))
    return made.id
      ? fixed(`#/spare-parts?view=robots&robot=${enc(made.id)}`, 'spare-parts')
      : fixed('#/spare-parts?view=robots', 'spare-parts');
  if ((m = path.match(/^\/api\/spare-parts\/robots\/(\d+)(\/parts(\/\d+)?)?$/)))
    return del && !m[2] ? gone('robot') : fixed(`#/spare-parts?view=robots&robot=${m[1]}`, 'spare-parts');
  if (/^\/api\/spare-parts\/(inventory|moves)\b/.test(path)) return fixed('#/spare-parts?view=inventory', 'spare-parts');
  if (/^\/api\/spare-parts\b/.test(path)) return fixed('#/spare-parts', 'spare-parts');

  // accounts, contacts, activity entries and their files
  if (/^\/api\/accounts$/.test(path))
    return made.id ? { target: { type: 'account', id: String(made.id) } } : none('The log does not say which account this created.');
  if (/^\/api\/accounts\/activities$/.test(path)) return none('The log does not say which account this activity was on.');
  if (/^\/api\/accounts\/contacts$/.test(path))
    return made.id ? { target: { type: 'contact', id: String(made.id) } } : none('The log does not say which contact this created.');
  if ((m = path.match(/^\/api\/accounts\/([^/]+)\/contacts(?:\/([^/]+))?$/))) {
    if (del) return gone('contact');
    const id = m[2] ? decodeURIComponent(m[2]) : made.id;
    return id ? { target: { type: 'contact', id: String(id) } } : { target: { type: 'account', id: decodeURIComponent(m[1]) } };
  }
  if ((m = path.match(/^\/api\/accounts\/([^/]+)\/activit(?:y|ies)(?:\/[^/]+)?$/)))
    return { target: { type: 'account', id: decodeURIComponent(m[1]) } };
  if ((m = path.match(/^\/api\/accounts\/([^/]+)$/)))
    return del ? gone('account') : { target: { type: 'account', id: decodeURIComponent(m[1]) } };
  if ((m = path.match(/^\/api\/crm\/activity\/([^/]+)\/attachments$/))) return { target: { type: 'entry', id: decodeURIComponent(m[1]) } };
  if (/^\/api\/crm\/attachments\/[^/]+$/.test(path))
    return snap.project_id
      ? { target: { type: 'entry', id: String(snap.project_id) } }
      : none('The log does not say which activity entry this file was on.');

  // tasks: their record
  if (/^\/api\/crm-actions(\/[^/]+)?$/.test(path)) return toTaskHome(made.id ? made : snap);

  // engineers, the calendar, the team
  if (/^\/api\/engineers$/.test(path))
    return made.id ? { target: { type: 'engineer', id: String(made.id) } } : fixed('#/engineers', 'engineers');
  if ((m = path.match(/^\/api\/engineers\/([^/]+)$/)))
    return del ? gone('engineer') : { target: { type: 'engineer', id: decodeURIComponent(m[1]) } };
  if (/^\/api\/(manpower|timesheets)\b/.test(path)) return fixed('#/engineers?view=calendar', 'engineers');
  if (/^\/api\/(users|engineer\/link)\b/.test(path)) return fixed('#/team', 'team');
  if (/^\/api\/engineer\/daily-reports/.test(path)) return fixed('#/daily-reports', 'daily-reports');

  // Staffing CRM
  if ((m = path.match(/^\/api\/staffing\/leads\/([^/]+)\/convert$/)))
    return { target: { type: 'staffing', entity: 'leads', id: decodeURIComponent(m[1]) } };
  if ((m = path.match(/^\/api\/staffing\/(accounts|contacts|leads)(?:\/([^/]+))?$/))) {
    if (del) return gone(`staffing ${STAFFING_NOUN[m[1]]}`);
    const id = m[2] ? decodeURIComponent(m[2]) : made.id;
    return id ? { target: { type: 'staffing', entity: m[1], id: String(id) } } : fixed(`#/crm-${m[1]}`, `crm-${m[1]}`);
  }
  if (/^\/api\/staffing\//.test(path)) return fixed('#/crm-dashboard', 'crm-dashboard');

  if (/^\/api\/svm\b/.test(path)) return fixed('#/checklists', 'checklists');
  if (/^\/api\/refs\b/.test(path)) return none('The dropdown lists have no page of their own.');
  if (/^\/api\/chat\b/.test(path)) return none('A question to the AI assistant - there is no record to open.');
  if (/^\/api\/ai\//.test(path)) return none('Reading a screenshot or text - there is no record to open.');
  if (/^\/api\/feedback\b/.test(path)) return none('A ticket sent by email - there is no record to open.');
  if (/^\/api\/ops-backup\b/.test(path)) return none('A backup run - there is no record to open.');
  return none('There is no page for this kind of change.');
}

/**
 * A jump checked against what the app has loaded. `ctx` holds Maps by id
 * (projects, accounts, contacts, engineers; entries: activity entry id ->
 * its account id) - a Map left out (null) means "not known", and the link is
 * followed unchecked - and canSee(tab).
 * -> { href, tab, verify? } | { text }. `verify` ({ url, pick }) is a record
 * the app does not hold (GLOBAL KSA, Staffing CRM): the screen reads it first.
 */
export function resolveJump(jump, ctx = {}) {
  if (!jump || jump.reason) return { text: (jump && jump.reason) || 'There is nothing to open.' };
  let out = jump.href ? { href: jump.href, tab: jump.tab } : null;
  const t = jump.target;
  const missing = (what) => ({
    text: jump.task ? `The ${what} this task was on no longer exists.` : `This ${what} no longer exists - it was deleted.`,
  });
  const has = (map, id) => (map ? map.get(String(id)) || null : undefined);
  if (t) {
    if (t.type === 'project' || t.type === 'record') {
      const p = has(ctx.projects, t.id);
      if (p === null && t.type === 'record') {
        if (has(ctx.accounts, t.id)) return resolveJump({ ...jump, target: { type: 'account', id: t.id } }, ctx);
        if (has(ctx.contacts, t.id)) return resolveJump({ ...jump, target: { type: 'contact', id: t.id } }, ctx);
        return missing('record');
      }
      if (p === null) return missing('record');
      const asProject = p ? isProjectRow(p) : false;
      const see = ctx.canSee || (() => true);
      const projectTab = asProject ? 'projects' : 'opportunities';
      const other = asProject ? 'opportunities' : 'projects';
      const tab = see(projectTab) || !see(other) ? projectTab : other;
      out = { href: tab === 'projects' ? projectHash(t.id) : opportunityHash(t.id), tab };
    } else if (t.type === 'account') {
      if (has(ctx.accounts, t.id) === null) return missing('account');
      out = { href: accountHash(t.id), tab: 'crm' };
    } else if (t.type === 'contact') {
      if (has(ctx.contacts, t.id) === null) return missing('contact');
      out = { href: contactHash(t.id), tab: 'crm' };
    } else if (t.type === 'entry') {
      const acct = has(ctx.entries, t.id);
      if (acct === null) return { text: 'This activity entry no longer exists - it was deleted.' };
      if (acct === undefined) return { text: 'The activity entry is not loaded - open its account from the Accounts tab.' };
      out = { href: accountHash(acct), tab: 'crm' };
    } else if (t.type === 'engineer') {
      if (has(ctx.engineers, t.id) === null) return { text: 'This engineer is not on the roster any more.' };
      out = { href: `#/engineers?engineer=${enc(t.id)}`, tab: 'engineers' };
    } else if (t.type === 'ksa') {
      out = {
        href: `#/ksa-overview?project=${enc(t.id)}`,
        tab: 'ksa-overview',
        verify: {
          url: '/api/ksa/projects',
          pick: (j) => findId(j, t.id),
          missing: 'This GLOBAL KSA record no longer exists - it was deleted.',
        },
      };
    } else if (t.type === 'staffing') {
      const noun = STAFFING_NOUN[t.entity] || 'record';
      out = {
        href: `#/crm-${t.entity}?record=${enc(t.id)}`,
        tab: `crm-${t.entity}`,
        verify: {
          url: `/api/staffing/${t.entity}/${enc(t.id)}`,
          pick: (j) => (j && j.id ? j : null),
          missing: `This staffing ${noun} no longer exists - it was deleted.`,
        },
      };
    }
  }
  if (!out) return { text: 'There is nothing to open.' };
  if (out.tab && ctx.canSee && !ctx.canSee(out.tab)) return { text: 'Your access does not include the page this happened on.' };
  return out;
}

/* ── revert: how an entry is undone ──────────────────────────────────── */

const MAIN = ['opportunities', 'projects'];
// The fields the projects route works out of others (routes/projects.js
// DERIVED_FROM, pinned): reverting an input brings them back by itself.
const DERIVED_FROM = FIELDS.derived;

/* A note's PUT always takes the text, so a severity-only revert sends the text it has now. */
function noteBody(send, cur) {
  const body = { ...send };
  if (body.body === undefined) body.body = cur ? cur.body : undefined;
  return body;
}

/*
 * A file's PUT (routes/attachments.js): a label always goes whole (type AND
 * number), and a relabel without `linked` unlinks the file, so `linked` is
 * always sent with it; a sub-folder decides its main folder, and a main folder
 * on its own clears the sub-folder.
 */
function fileBody(send, cur, row) {
  const snap = (row.detail || {}).snapshot || {};
  const body = {};
  if ('doc_type' in send || 'doc_ref' in send) {
    body.doc_type = 'doc_type' in send ? send.doc_type : cur ? cur.doc_type : snap.doc_type;
    body.doc_ref = 'doc_ref' in send ? send.doc_ref : cur ? cur.doc_ref : (snap.doc_ref ?? null);
    body.linked = 'linked' in send ? send.linked : !!(cur && cur.linked);
  } else if ('linked' in send) body.linked = send.linked;
  if ('subfolder_id' in send && send.subfolder_id) body.subfolder_id = send.subfolder_id;
  else if ('subfolder_id' in send || 'folder' in send) {
    body.folder = 'folder' in send ? send.folder : cur ? cur.folder : null;
    if ('subfolder_id' in send) body.subfolder_id = null;
  }
  return body;
}

/*
 * A purchase's rate (routes/ksa.js normalizePurchase): a typed bank rate goes
 * back as `rate`; the month's rate (or the estimate it fell back to) as
 * rate_source 'monthly', which the server works out again.
 */
function purchaseBody(send, cur, row) {
  const ch = (row.detail || {}).changes || {};
  const body = { ...send };
  delete body.rate;
  delete body.rate_source;
  if (ch.rate || ch.rate_source) {
    const source = ch.rate_source ? ch.rate_source.from : cur && cur.rate_source;
    const rate = ch.rate ? restoreValue(ch.rate.from) : { ok: false };
    if (source === 'actual' && rate.ok && rate.value) body.rate = rate.value;
    else body.rate_source = 'monthly';
  }
  return body;
}

/**
 * The edits the log can undo, by path (PUT / PATCH with `changes`). Each:
 *   areas    any of these at Edit may revert (src/data/roles canEdit); manager: access managers only
 *   fields   the columns the route writes (revertFields.json, pinned to the routes
 *            by server/test_activity_revert.js); any other changed column the
 *            server keeps itself
 *   lists / bools   how a logged value goes back (restoreValue)
 *   expect   the route has the r50 conflict guard (`_expect` / `_force`)
 *   read     where the record's values now are: (params, row) -> { url, pick }
 *   url      where the revert is sent: (params, cur) -> url (default: the entry's own path)
 */
const UPDATES = [
  {
    // a main record's invoicing columns are the invoice ledger's (10/11/26):
    // the server refuses them, so a revert leaves them out
    key: 'project',
    re: /^\/api\/projects\/([^/]+)$/,
    areas: MAIN,
    noun: 'record',
    expect: true,
    fields: FIELDS.project.filter((f) => !FIELDS.projectLedger.includes(f)),
    bools: ['quote_sent'],
    follows: { topic: 'Name' },
    derived: DERIVED_FROM,
    read: ([id]) => ({ url: '/api/projects', pick: (j) => findId(j, decodeURIComponent(id)) }),
  },
  {
    key: 'ksa-project',
    re: /^\/api\/ksa\/projects\/([^/]+)$/,
    areas: ['ksa'],
    noun: 'GLOBAL KSA record',
    expect: true,
    fields: FIELDS.project.filter((f) => f !== 'account_id' && f !== 'parent_id'),
    bools: ['quote_sent'],
    follows: { topic: 'Name' },
    derived: DERIVED_FROM,
    read: ([id]) => ({ url: '/api/ksa/projects', pick: (j) => findId(j, decodeURIComponent(id)) }),
  },
  {
    key: 'note',
    re: /^\/api\/projects\/([^/]+)\/comments\/([^/]+)$/,
    areas: MAIN,
    noun: 'note',
    fields: ['body', 'severity'],
    read: ([pid, cid]) => ({ url: `/api/projects/${pid}/comments`, pick: (j) => findId(j, decodeURIComponent(cid)) }),
    build: noteBody,
  },
  {
    key: 'ksa-note',
    re: /^\/api\/ksa\/projects\/([^/]+)\/comments\/([^/]+)$/,
    areas: ['ksa'],
    noun: 'GLOBAL KSA note',
    fields: ['body', 'severity'],
    read: ([pid, cid]) => ({ url: `/api/ksa/projects/${pid}/comments`, pick: (j) => findId(j, decodeURIComponent(cid)) }),
    build: noteBody,
  },
  {
    key: 'purchase',
    re: /^\/api\/ksa\/projects\/([^/]+)\/purchases\/([^/]+)$/,
    areas: ['ksa'],
    noun: 'GLOBAL KSA purchase',
    fields: [...FIELDS.purchase, 'purchase_date', 'amount', 'rate', 'rate_source'],
    read: ([pid, id]) => ({ url: `/api/ksa/projects/${pid}/purchases`, pick: (j) => findId(j && j.purchases, decodeURIComponent(id)) }),
    build: purchaseBody,
  },
  {
    key: 'file',
    re: /^\/api\/attachments\/([^/]+)$/,
    areas: MAIN,
    noun: 'file',
    fields: ['doc_type', 'doc_ref', 'linked', 'folder', 'subfolder_id'],
    bools: ['linked'],
    read: ([id], row) => {
      const pid = ((row.detail || {}).snapshot || {}).project_id;
      return pid ? { url: `/api/projects/${enc(pid)}/attachments`, pick: (j) => findId(j, decodeURIComponent(id)) } : null;
    },
    build: fileBody,
  },
  {
    key: 'ksa-file',
    re: /^\/api\/ksa\/attachments\/([^/]+)$/,
    areas: ['ksa'],
    noun: 'GLOBAL KSA file',
    fields: ['doc_type', 'doc_ref', 'linked', 'folder', 'subfolder_id'],
    bools: ['linked'],
    read: ([id], row) => {
      const pid = ((row.detail || {}).snapshot || {}).project_id;
      return pid ? { url: `/api/ksa/projects/${enc(pid)}/attachments`, pick: (j) => findId(j, decodeURIComponent(id)) } : null;
    },
    build: fileBody,
  },
  {
    key: 'account',
    re: /^\/api\/accounts\/([^/]+)$/,
    areas: ['accounts'],
    noun: 'account',
    fields: FIELDS.account,
    lists: ['customer_type', 'robot_brands'],
    bools: ['stage_review', 'needs_details', 'auto_created'],
    read: ([id]) => ({ url: '/api/accounts', pick: (j) => findId(j, decodeURIComponent(id)) }),
  },
  {
    // a contact moved to another account lives under that one now
    key: 'contact',
    re: /^\/api\/accounts\/([^/]+)\/contacts\/([^/]+)$/,
    areas: ['accounts'],
    noun: 'contact',
    fields: [...FIELDS.contact, 'account_id'],
    bools: ['is_primary'],
    read: ([, cid]) => ({ url: '/api/accounts/contacts/all', pick: (j) => findId(j, decodeURIComponent(cid)) }),
    url: ([aid, cid], cur) => `/api/accounts/${cur && cur.account_id ? enc(cur.account_id) : aid}/contacts/${cid}`,
  },
  {
    key: 'task',
    re: /^\/api\/crm-actions\/([^/]+)$/,
    areas: ['tasks'],
    noun: 'task',
    fields: [...FIELDS.task, 'status', 'tagged'],
    lists: ['tagged'],
    read: ([id], row) => {
      const home = ((row.detail || {}).snapshot || {}).entity_id;
      return { url: home ? `/api/crm-actions?entity_id=${enc(home)}` : '/api/crm-actions', pick: (j) => findId(j, decodeURIComponent(id)) };
    },
  },
  {
    key: 'ksa-task',
    re: /^\/api\/ksa\/crm-actions\/([^/]+)$/,
    areas: ['ksa'],
    noun: 'GLOBAL KSA task',
    fields: [...FIELDS.ksaTask, 'status', 'tagged'],
    lists: ['tagged'],
    read: ([id], row) => {
      const home = ((row.detail || {}).snapshot || {}).entity_id;
      return {
        url: home ? `/api/ksa/crm-actions?entity_id=${enc(home)}` : '/api/ksa/crm-actions',
        pick: (j) => findId(j, decodeURIComponent(id)),
      };
    },
  },
  {
    key: 'engineer',
    re: /^\/api\/engineers\/([^/]+)$/,
    areas: ['engineers'],
    noun: 'engineer',
    fields: FIELDS.engineer,
    read: ([id]) => ({ url: '/api/engineers?includeArchived=1', pick: (j) => findId(j, decodeURIComponent(id)) }),
  },
  {
    key: 'staffing',
    re: /^\/api\/staffing\/(accounts|contacts|leads)\/([^/]+)$/,
    areas: ['staffing'],
    noun: 'staffing record',
    fieldsOf: ([entity]) => FIELDS.staffing[entity],
    read: ([entity, id]) => ({ url: `/api/staffing/${entity}/${id}`, pick: (j) => (j && j.id ? j : null) }),
  },
  {
    key: 'role',
    re: /^\/api\/users\/([^/]+)$/,
    manager: true,
    noun: 'person',
    fields: ['role'],
    read: ([email]) => ({ url: `/api/users/${email}/access`, pick: (j) => (j && j.role ? { role: j.role } : null) }),
  },
  // Spare Parts (10/8/26). A reverted price sends its old check date with it,
  // so the route puts that date back instead of stamping today.
  {
    key: 'spare-part',
    re: /^\/api\/spare-parts\/parts\/(\d+)$/,
    areas: ['spare-parts'],
    noun: 'spare part',
    fields: FIELDS.sparePart,
    read: ([id]) => ({ url: `/api/spare-parts/parts/${id}`, pick: (j) => (j && j.part ? j.part : null) }),
  },
  {
    key: 'spare-robot',
    re: /^\/api\/spare-parts\/robots\/(\d+)$/,
    areas: ['spare-parts'],
    noun: 'robot',
    fields: FIELDS.spareRobot,
    read: ([id]) => ({ url: `/api/spare-parts/robots/${id}`, pick: (j) => (j && j.robot ? j.robot : null) }),
  },
  {
    key: 'spare-stock',
    re: /^\/api\/spare-parts\/inventory\/(\d+)$/,
    areas: ['spare-parts'],
    noun: 'stock row',
    fields: FIELDS.spareInventory,
    read: ([id]) => ({ url: `/api/spare-parts/inventory/${id}`, pick: (j) => (j && j.item ? j.item : null) }),
  },
];

/* A created record, by the path that made it: the DELETE that removes it. */
const CREATES = [
  { re: /^\/api\/projects$/, url: (m, id) => `/api/projects/${enc(id)}`, areas: MAIN, noun: 'record' },
  {
    re: /^\/api\/ksa\/projects$/,
    url: (m, id) => `/api/ksa/projects/${enc(id)}`,
    areas: ['ksa'],
    noun: 'GLOBAL KSA record',
    warn: 'Its notes, tasks and files go with it.',
  },
  { re: /^\/api\/projects\/([^/]+)\/comments$/, url: (m, id) => `/api/projects/${m[1]}/comments/${enc(id)}`, areas: MAIN, noun: 'note' },
  {
    re: /^\/api\/ksa\/projects\/([^/]+)\/comments$/,
    url: (m, id) => `/api/ksa/projects/${m[1]}/comments/${enc(id)}`,
    areas: ['ksa'],
    noun: 'GLOBAL KSA note',
  },
  {
    re: /^\/api\/projects\/([^/]+)\/attachments$/,
    url: (m, id) => `/api/attachments/${enc(id)}`,
    areas: MAIN,
    noun: 'file',
    warn: 'The file is deleted for good (its SharePoint backup copy moves to the archive).',
  },
  {
    re: /^\/api\/ksa\/projects\/([^/]+)\/attachments$/,
    url: (m, id) => `/api/ksa/attachments/${enc(id)}`,
    areas: ['ksa'],
    noun: 'GLOBAL KSA file',
    warn: 'The file is deleted for good (its SharePoint backup copy moves to the archive).',
  },
  {
    re: /^\/api\/(?:projects|attachments)\/([^/]+)\/folders$/,
    url: (m, id) => `/api/attachments/folders/${enc(id)}`,
    areas: MAIN,
    noun: 'Doc Hub folder',
    warn: 'Its files move back to the main folder.',
  },
  {
    re: /^\/api\/ksa\/(?:projects|attachments)\/([^/]+)\/folders$/,
    url: (m, id) => `/api/ksa/attachments/folders/${enc(id)}`,
    areas: ['ksa'],
    noun: 'GLOBAL KSA Doc Hub folder',
    warn: 'Its files move back to the main folder.',
  },
  {
    re: /^\/api\/accounts$/,
    url: (m, id) => `/api/accounts/${enc(id)}`,
    areas: ['accounts'],
    noun: 'account',
    warn: 'Its sites go with it, with their activity entries and files.',
  },
  {
    re: /^\/api\/accounts\/([^/]+)\/contacts$/,
    url: (m, id) => `/api/accounts/${m[1]}/contacts/${enc(id)}`,
    areas: ['accounts'],
    noun: 'contact',
  },
  {
    re: /^\/api\/accounts\/contacts$/,
    url: (m, id, made) => (made.account_id ? `/api/accounts/${enc(made.account_id)}/contacts/${enc(id)}` : null),
    areas: ['accounts'],
    noun: 'contact',
  },
  {
    re: /^\/api\/accounts\/([^/]+)\/activity$/,
    url: (m, id) => `/api/accounts/${m[1]}/activity/${enc(id)}`,
    areas: ['accounts'],
    noun: 'activity entry',
    warn: 'Its files go with it. A next-step task made with it stays.',
  },
  {
    re: /^\/api\/crm\/activity\/([^/]+)\/attachments$/,
    url: (m, id) => `/api/crm/attachments/${enc(id)}`,
    areas: ['accounts'],
    noun: 'activity file',
    warn: 'The file is deleted for good.',
  },
  { re: /^\/api\/crm-actions$/, url: (m, id) => `/api/crm-actions/${enc(id)}`, areas: ['tasks'], noun: 'task' },
  { re: /^\/api\/ksa\/crm-actions$/, url: (m, id) => `/api/ksa/crm-actions/${enc(id)}`, areas: ['ksa'], noun: 'GLOBAL KSA task' },
  { re: /^\/api\/engineers$/, url: (m, id) => `/api/engineers/${enc(id)}`, areas: ['engineers'], noun: 'engineer' },
  {
    re: /^\/api\/staffing\/(accounts|contacts|leads|activities|notes)$/,
    url: (m, id) => `/api/staffing/${m[1]}/${enc(id)}`,
    areas: ['staffing'],
    noun: 'staffing record',
  },
  { re: /^\/api\/quote-rates\/catalog$/, url: (m, id) => `/api/quote-rates/catalog/${enc(id)}`, areas: ['quotes'], noun: 'catalog part' },
  {
    re: /^\/api\/ksa\/quote-rates\/catalog$/,
    url: (m, id) => `/api/ksa/quote-rates/catalog/${enc(id)}`,
    areas: ['quotes'],
    see: ['ksa'],
    noun: 'GLOBAL KSA catalog part',
  },
  {
    re: /^\/api\/spare-parts\/parts$/,
    url: (m, id) => `/api/spare-parts/parts/${enc(id)}`,
    areas: ['spare-parts'],
    noun: 'spare part',
    warn: 'Refused while a robot list or a stock row still holds it.',
  },
  {
    re: /^\/api\/spare-parts\/robots$/,
    url: (m, id) => `/api/spare-parts/robots/${enc(id)}`,
    areas: ['spare-parts'],
    noun: 'robot',
    warn: 'Its recommended spare parts list goes with it.',
  },
  {
    re: /^\/api\/spare-parts\/robots\/(\d+)\/parts$/,
    url: (m, id) => `/api/spare-parts/robots/${m[1]}/parts/${enc(id)}`,
    areas: ['spare-parts'],
    noun: "line on the robot's list",
  },
  {
    re: /^\/api\/spare-parts\/inventory$/,
    url: (m, id) => `/api/spare-parts/inventory/${enc(id)}`,
    areas: ['spare-parts'],
    noun: 'stock row',
    warn: 'Only once its stock is out: take it out with a move first, so the log says where it went.',
  },
];

/* Entries that can never be undone here, and why. First match wins. */
const NEVER = [
  [/^\/api\/manpower\b/, 'Calendar saves log only that the schedule changed, not the days as they were, so they cannot be undone here.'],
  [/^\/api\/users\/me$/, 'A sign-in - there is nothing to undo.'],
  [/^\/api\/chat\b/, 'A question to the AI assistant - there is nothing to undo.'],
  [/^\/api\/ai\//, 'Reading a screenshot or text changes nothing - there is nothing to undo.'],
  [/^\/api\/feedback\b/, 'A ticket was emailed - it cannot be unsent.'],
  [/^\/api\/ops-backup\b/, 'A backup run cannot be undone.'],
  [/^\/api\/(ksa\/)?quotes\/(generate|pdf)\b/, 'A quote document was downloaded - nothing was saved.'],
  [/^\/api\/(ksa\/)?quotes\/drafts\//, 'The log does not keep what the quote draft said before.'],
  [/^\/api\/(ksa\/)?quote-rates\/?$/, 'The log does not keep the rates as they were before this save.'],
  [
    /^\/api\/spare-parts\/inventory\/\d+\/moves$/,
    'A stock move is undone with another move (Spare Parts > Inventory), so the log keeps where the parts went.',
  ],
  [/^\/api\/spare-parts\/parts\/\d+\/price-check$/, 'A price check is undone by checking again or typing the old price on the part.'],
  [/^\/api\/spare-parts\/import$/, 'An import changes many parts at once - undo it part by part on Spare Parts.'],
  [/^\/api\/ksa\/projects\/[^/]+\/purchases$/, "The log does not keep the new purchase's id, so it cannot be found to remove."],
  [/^\/api\/ksa\/fx-rates\b/, 'The log does not keep the exchange rate as it was.'],
  [/^\/api\/refs\b/, 'The log does not say which dropdown list this was, or what it held before.'],
  [
    /^\/api\/finance-invoices\b/,
    'Invoices and payments are put right on the Finance Invoice tab: edit or void the invoice, or change or delete the payment.',
  ],
  [/^\/api\/projects\/[^/]+\/invoices$/, 'An invoice is put right on the Finance Invoice tab: edit or void it there.'],
  [/^\/api\/svm\b/, "The log does not keep a checklist's earlier state."],
  [/^\/api\/engineer\/daily-reports\b/, 'A submitted daily report cannot be withdrawn here.'],
  [/^\/api\/engineer\/link\b/, "The log does not keep the engineer's earlier link."],
  [
    /^\/api\/staffing\/leads\/[^/]+\/convert$/,
    'Finishing a lead makes an account and a contact at once - undo it by hand on the Staffing CRM.',
  ],
  [/\/(assign|unassign)-engineer$/, 'This route no longer exists.'],
];

const DELETE_REASON =
  "Can't undo a delete: the log keeps only a few identifying details of a deleted record, not the whole of it, so it cannot be put back as it was.";

const updateSpecOf = (path) => {
  for (const spec of UPDATES) {
    const m = path.match(spec.re);
    if (m && !(spec.key === 'role' && m[1] === 'me')) return { spec, params: m.slice(1) };
  }
  return null;
};

/**
 * How an entry is undone:
 *   { kind: 'update', key, areas | manager, noun, expect }
 *   { kind: 'delete', request: { method: 'DELETE', url }, areas, del: true, noun, warn }
 *   { kind: null, reason }
 */
export function revertOf(row) {
  const method = row.method;
  const path = segs(row.path);
  const d = row.detail || {};
  for (const [re, reason] of NEVER) if (re.test(path)) return { kind: null, reason };

  if (method === 'DELETE') return { kind: null, reason: DELETE_REASON };

  if (method === 'POST') {
    const made = d.created || {};
    for (const c of CREATES) {
      const m = path.match(c.re);
      if (!m) continue;
      if (made.id === undefined || made.id === null || made.id === '')
        return { kind: null, reason: 'The log does not keep what this created, so it cannot be found to remove.' };
      const url = c.url(m, made.id, made);
      if (!url) return { kind: null, reason: 'The log does not say which account the contact was added to.' };
      return {
        kind: 'delete',
        request: { method: 'DELETE', url },
        areas: c.areas,
        see: c.see,
        del: true,
        noun: c.noun,
        warn: c.warn || null,
      };
    }
    return { kind: null, reason: 'The log does not keep what this created, so it cannot be removed from here.' };
  }

  // PUT / PATCH
  const access = path.match(/^\/api\/users\/([^/]+)\/access$/);
  if (access) {
    if (d.access_key && d.override_was && d.override_to) return { kind: 'update', key: 'access', manager: true, noun: 'access' };
    if (d.manage_access && d.manage_access_was) return { kind: 'update', key: 'manage', manager: true, noun: 'access' };
    if (d.access_reset)
      return { kind: null, reason: 'A reset to the role does not log which overrides it removed, so it cannot be put back.' };
    return { kind: null, reason: 'This entry was logged before undo was possible: it does not say what the access was before.' };
  }
  const hit = updateSpecOf(path);
  if (!hit) return { kind: null, reason: 'The log did not keep the values from before this edit.' };
  if (!d.changes || !Object.keys(d.changes).length)
    return { kind: null, reason: 'Nothing changed in this save, so there is nothing to revert.' };
  const plan = planUpdate(row, undefined);
  if (!plan.request && !plan.nothing) {
    return { kind: null, reason: `Nothing here can be put back: ${plan.skipped.join('; ')}.` };
  }
  const { spec } = hit;
  return { kind: 'update', key: spec.key, areas: spec.areas, manager: !!spec.manager, noun: spec.noun, expect: !!spec.expect };
}

/** Where to read the record's values now, before an update revert: { url, pick } or null. */
export function readOf(row) {
  const path = segs(row.path);
  if (/^\/api\/users\/[^/]+\/access$/.test(path)) {
    const email = path.split('/')[3];
    return { url: `/api/users/${email}/access`, pick: (j) => (j && Array.isArray(j.areas) ? j : null) };
  }
  const hit = updateSpecOf(path);
  return hit && hit.spec.read ? hit.spec.read(hit.params, row) : null;
}

const levelWord = (v) => (v === 'none' || v === null || v === undefined ? 'role default' : LEVEL_LABELS[v] || v);

/* Access entries (PUT /api/users/:email/access): the override or the
   "can manage access" switch, set back as it was. */
function planAccess(row, cur) {
  const d = row.detail || {};
  const url = segs(row.path);
  const out = { lines: [], already: [], drifted: [], skipped: [], nothing: false, force: false, request: null };
  if (d.access_key) {
    const from = d.override_was === 'none' ? null : d.override_was;
    const to = d.override_to === 'none' ? null : d.override_to;
    const label = `${d.access_area || fieldLabel(d.access_key)} access`;
    const area = cur && Array.isArray(cur.areas) ? cur.areas.find((a) => a.key === d.access_key) : undefined;
    const now = area ? (area.override ?? null) : undefined;
    if (now !== undefined && now === from) {
      out.already.push(`${label} is already ${levelWord(from)}`);
      out.nothing = true;
      return out;
    }
    if (now !== undefined && now !== to) {
      out.drifted.push(`${label} is ${levelWord(now)} now (this entry set ${levelWord(to)})`);
      out.force = true;
    }
    out.lines.push(`${label}: ${levelWord(now !== undefined ? now : to)} → ${levelWord(from)}`);
    out.request = { method: 'PUT', url, body: { area: d.access_key, level: from } };
    return out;
  }
  const from = d.manage_access_was === 'on';
  const to = d.manage_access === 'on';
  const word = (v) => (v ? 'On' : 'Off');
  const now = cur && typeof cur.canManageAccess === 'boolean' ? cur.canManageAccess : undefined;
  if (now !== undefined && now === from) {
    out.already.push(`Can manage access is already ${word(from)}`);
    out.nothing = true;
    return out;
  }
  out.lines.push(`Can manage access: ${word(now !== undefined ? now : to)} → ${word(from)}`);
  out.request = { method: 'PUT', url, body: { canManageAccess: from } };
  return out;
}

/**
 * The revert of an edit against the record as it is now (`cur`: its row from
 * the API; undefined when it could not be read, which only a route with the
 * `_expect` guard goes ahead without).
 * -> {
 *   request   { method, url, body } or null when there is nothing to send
 *   lines     what will be set, "Status: Won → Open"
 *   already   fields that already have their earlier value (left out)
 *   drifted   fields someone changed again since (the confirm asks first)
 *   skipped   changed fields an undo leaves alone, and why
 *   nothing   every field already has its earlier value
 *   force     drifted is not empty: sending means "Revert anyway"
 * }
 */
export function planUpdate(row, cur) {
  const path = segs(row.path);
  if (/^\/api\/users\/[^/]+\/access$/.test(path)) return planAccess(row, cur);
  const out = { lines: [], already: [], drifted: [], skipped: [], nothing: false, force: false, request: null };
  const hit = updateSpecOf(path);
  if (!hit) return out;
  const { spec, params } = hit;
  const changes = (row.detail || {}).changes || {};
  const fields = spec.fieldsOf ? spec.fieldsOf(params) || [] : spec.fields;
  const kindOf = (col) => ((spec.lists || []).includes(col) ? 'list' : (spec.bools || []).includes(col) ? 'bool' : 'text');
  const known = cur !== undefined && cur !== null;

  // what can go back at all
  const back = {};
  const serverKept = [];
  for (const [col, ch] of Object.entries(changes)) {
    if (!fields.includes(col)) {
      serverKept.push(fieldLabel(col));
      continue;
    }
    if (spec.follows && spec.follows[col]) {
      out.skipped.push(`${fieldLabel(col)} (it follows the ${spec.follows[col]})`);
      continue;
    }
    const r = restoreValue(ch && ch.from, kindOf(col));
    if (!r.ok) {
      out.skipped.push(`${fieldLabel(col)} (${r.why})`);
      continue;
    }
    back[col] = r.value;
  }
  // a worked-out field comes back with its inputs; on its own it is sent as it was
  for (const [col, inputs] of Object.entries(spec.derived || {})) {
    if (col in back && inputs.some((k) => k in back)) {
      delete back[col];
      out.skipped.push(`${fieldLabel(col)} (worked out again from ${inputs.map(fieldLabel).join(', ')})`);
    }
  }
  if (serverKept.length) out.skipped.push(`${serverKept.join(', ')} (kept by the server itself)`);

  const send = {};
  const expect = {};
  for (const [col, value] of Object.entries(back)) {
    const ch = changes[col];
    const now = known ? cur[col] : undefined;
    const shownFrom = fmtVal(value, col);
    if (known && sameValue(now, ch.from)) {
      out.already.push(`${fieldLabel(col)} is already ${shownFrom}`);
      continue;
    }
    if (known && !sameValue(now, ch.to)) {
      out.drifted.push(`${fieldLabel(col)} is ${fmtVal(now, col)} now (this entry set ${fmtVal(ch.to, col)})`);
      out.force = true;
    }
    send[col] = value;
    out.lines.push(`${fieldLabel(col)}: ${fmtVal(known ? now : ch.to, col)} → ${shownFrom}`);
    const to = restoreValue(ch.to, kindOf(col));
    if (spec.expect && to.ok && (ch.to === null || ch.to === undefined || String(ch.to).length < LOG_MAX)) expect[col] = to.value;
  }
  if (!Object.keys(send).length) {
    out.nothing = Object.keys(back).length > 0;
    return out;
  }
  const body = spec.build ? spec.build(send, known ? cur : null, row) : { ...send };
  if (spec.expect) {
    if (out.force) body._force = true;
    else body._expect = expect;
  }
  const url = spec.url ? spec.url(params, known ? cur : null) : path;
  out.request = { method: row.method === 'PATCH' ? 'PATCH' : 'PUT', url, body };
  return out;
}

/** The same request, sent over a conflict ("Revert anyway"). */
export function forced(request) {
  const body = { ...(request.body || {}) };
  delete body._expect;
  body._force = true;
  return { ...request, body };
}

/** The confirm step of a create's revert: { request, lines, warn }. */
export function deletePlan(row, rv = revertOf(row)) {
  const made = (row.detail || {}).created || {};
  const what = identText(made);
  return {
    request: rv.request,
    lines: [`Delete the ${rv.noun}${what ? ` ${what}` : ''} (${made.id})`],
    warn: rv.warn || null,
  };
}

/** The 409 edit_conflict a revert met, as lines: "Status is Lost now (Paul Cooke changed it)". */
export function conflictLines(body = {}) {
  const who = body.changed_by && body.changed_by.actor ? ` (${body.changed_by.actor} changed it)` : '';
  return (body.conflicts || []).map((c) => `${fieldLabel(c.field)} is ${fmtVal(c.theirs, c.field)} now${who}`);
}

const areaLabel = (key) => (AREAS.find((a) => a.key === key) || {}).label || key;

/** May this person revert it? { ok } or { ok: false, why }. The server checks again. */
export function mayRevert(rv, access) {
  if (!rv || !rv.kind) return { ok: false, why: (rv && rv.reason) || 'This cannot be reverted.' };
  if (rv.manager) {
    return access && access.canManageAccess
      ? { ok: true }
      : { ok: false, why: 'Only people who can manage access may revert role and access changes.' };
  }
  const allowed = rv.del ? (a) => canDelete(access, a) : (a) => canEdit(access, a);
  if (!(rv.areas || []).some(allowed)) {
    const words = (rv.areas || []).map(areaLabel).join(' or ');
    return { ok: false, why: `Reverting this needs ${rv.del ? 'delete rights' : 'Edit'} on ${words}.` };
  }
  const blind = (rv.see || []).find((a) => !hasArea(access, a, 'view'));
  if (blind) return { ok: false, why: `Reverting this also needs ${areaLabel(blind)}.` };
  return { ok: true };
}
